import { KeyboardEvent, useEffect, useState } from 'react';
import { AutocompleteList } from '../../components/AutocompleteList/AutocompleteList';
import { useFormSuggestions } from '../../hooks/useFormSuggestions';
import { usePrevious } from '../../hooks/usePrevious';
import { useFormatting, useTranslation } from '../../i18n/i18n';
import { useSession } from '../../state/session';
import { AddressSuggestion, DeliveryTypeCompany } from '../../types';
import './DeliveryAddressInput.css';
import { useFormValidationContainer } from './FormValidatorContainer';
import { InputAlertMessage } from './InputAlertMessage';
import { InputGrid, halfRow } from './InputGrid';
import { SelectLastAddressesInput } from './SelectLastAddressesInput';
import { TextInput } from './TextInput';

const DOOR_CODE_MAX_LENGTH = 16;

export type DeliveryAddressModel = {
	company: string;
	street: string;
	careOf: string;
	doorCode: string;
	firstname: string;
	lastname: string;
	phone: string;
	zip: string;
	city: string;
};

type DeliveryAddressInputProps = {
	onBlur?: (e: any) => void;
	onKeyPress?: (event: KeyboardEvent<HTMLInputElement>) => void;
	deliveryType: DeliveryTypeCompany;
	deliveryAddresses: DeliveryAddressModel[];
	selectedAddress: string;
	setSelectedAddress: React.Dispatch<React.SetStateAction<string>>;
};

export const DeliveryAddressInput = ({
	onKeyPress,
	onBlur,
	deliveryType,
	deliveryAddresses,
	selectedAddress,
	setSelectedAddress,
}: DeliveryAddressInputProps): JSX.Element => {
	const t = useTranslation().form;
	const tAccount = useTranslation().accountPage;
	const { asZip } = useFormatting();
	const { checkCompany, setCompanySearchResults, getAddressSuggestions, addressSuggestions, filterUniqueZip } =
		useFormSuggestions();

	const { isLoggedIn } = useSession();
	const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
	const form = useFormValidationContainer();
	const [deliveryZipIsFocused, setDeliveryZipIsFocused] = useState(false);
	const [showCareOfInput, setShowCareOfInput] = useState(!!form.deliveryCareOf.value);
	const [showDoorCodeInput, setShowDoorCodeInput] = useState(!!form.deliveryDoorCode.value);
	const previousShowCareOfInput = usePrevious(showCareOfInput);
	const previousShowDoorCodeInput = usePrevious(showDoorCodeInput);

	useEffect(() => {
		if (!showCareOfInput && showCareOfInput !== previousShowCareOfInput) {
			if (onBlur) onBlur({} as unknown as Event);
		}
	}, [showCareOfInput, previousShowCareOfInput]);

	useEffect(() => {
		if (!showDoorCodeInput && showDoorCodeInput !== previousShowDoorCodeInput) {
			if (onBlur) onBlur({} as unknown as Event);
		}
	}, [showDoorCodeInput, previousShowDoorCodeInput]);

	useEffect(() => {
		if (form.deliveryDoorCode.value !== '' && !showDoorCodeInput) {
			setShowDoorCodeInput(true);
		}
	}, [form.deliveryDoorCode.value]);

	useEffect(() => {
		if (form.deliveryCareOf.value !== '' && !showCareOfInput) {
			setShowCareOfInput(true);
		}
	}, [form.deliveryCareOf.value]);

	useEffect(() => {
		if (form.deliveryStreet.value !== '') getAddressSuggestions(form.deliveryStreet.value);
	}, []);

	useEffect(() => {
		setShowAddressSuggestions(addressSuggestions.length > 0 && deliveryZipIsFocused);
	}, [addressSuggestions, deliveryZipIsFocused]); // eslint-disable-line react-hooks/exhaustive-deps

	async function handleCompanyBlur(inputValue: string) {
		if (inputValue.length < 3) {
			setCompanySearchResults(undefined);
			return;
		}

		setCompanySearchResults(await checkCompany(inputValue));
	}

	function handlePickAddress(e: React.ChangeEvent<{ id: unknown }>) {
		if (e.target.id && typeof e.target.id === 'string') {
			setSelectedAddress(e.target.id);
			const newAddress = deliveryAddresses.find((address: any) => address.id == e.target.id);

			if (newAddress) {
				const { careOf, doorCode } = newAddress;

				form.fillAddressForm(newAddress);

				if (careOf === '') {
					setShowCareOfInput(false);
				}

				if (doorCode === '') {
					setShowDoorCodeInput(false);
				}
			} else {
				form.resetAddressForm();
			}
		}
	}

	return (
		<InputGrid>
			{isLoggedIn && deliveryAddresses && deliveryAddresses.length > 1 && (
				<SelectLastAddressesInput
					onSelectAddress={handlePickAddress}
					selectedAddress={selectedAddress.toString()}
					deliveryAddresses={deliveryAddresses}
				/>
			)}
			<TextInput
				id="delivery-company"
				autocomplete="organization"
				placeholder={tAccount.companyName}
				value={form.deliveryCompany.value}
				maxLength={64}
				// value={currentAddress.company}
				isValid={form.deliveryCompany.isValid}
				onKeyDown={onKeyPress}
				nextElement={showCareOfInput ? 'delivery-careof' : 'delivery-reference'}
				onChange={form.deliveryCompany.handleOnChange}
				onBlur={(e) => {
					form.deliveryCompany.validate();
					handleCompanyBlur(e.target.value);
					if (onBlur) onBlur(e);
				}}
				toggleableLabel={
					!showCareOfInput
						? {
								text: t.addCareOf,
								onClick: () => setShowCareOfInput(true),
								id: 'care-of-toggle',
							}
						: undefined
				}
			/>
			{form.deliveryCompany.type !== 'success' && (
				<InputAlertMessage type={form.deliveryCompany.type} message={form.deliveryCompany.message} />
			)}
			{showCareOfInput && (
				<>
					<TextInput
						id="delivery-careof"
						placeholder={t.careOf}
						value={form.deliveryCareOf.value}
						maxLength={64}
						onKeyDown={onKeyPress}
						autoFocus={showCareOfInput}
						nextElement="firstname"
						onChange={form.deliveryCareOf.handleOnChange}
						onBlur={onBlur}
						toggleableLabel={{
							text: t.removeCareOf,
							onClick: () => {
								setShowCareOfInput(false);
								form.deliveryCareOf.setValue('');
							},
							id: 'care-of-toggle-remove',
						}}
					/>
				</>
			)}
			<TextInput
				id="firstname"
				placeholder={t.firstname}
				value={form.deliveryFirstname.value}
				maxLength={64}
				onChange={form.deliveryFirstname.handleOnChange}
				onBlur={onBlur}
				onKeyDown={onKeyPress}
				nextElement="lastname"
				className={halfRow}
			/>
			<TextInput
				id="lastname"
				placeholder={t.lastname}
				value={form.deliveryLastname.value}
				onBlur={onBlur}
				onChange={form.deliveryLastname.handleOnChange}
				onKeyDown={onKeyPress}
				nextElement="phone"
				className={halfRow}
				maxLength={64}
			/>
			<TextInput
				id="delivery-street"
				autocomplete="street-address"
				placeholder={t.street}
				value={form.deliveryStreet.value}
				maxLength={64}
				onChange={form.deliveryStreet.handleOnChange}
				isValid={form.deliveryStreet.isValid}
				onBlur={(e) => {
					form.deliveryStreet.validate();
					if (onBlur) onBlur(e);
					e.target.value.length > 0 ? getAddressSuggestions(e.target.value) : undefined;
				}}
				onKeyDown={onKeyPress}
				nextElement={showDoorCodeInput ? 'delivery-doorcode' : 'delivery-zip'}
				toggleableLabel={
					!showDoorCodeInput
						? {
								text: t.addDoorCode,
								onClick: () => setShowDoorCodeInput(true),
								id: 'zip-toggle',
							}
						: undefined
				}
			/>
			{form.deliveryStreet.type !== 'success' && (
				<InputAlertMessage type={form.deliveryStreet.type} message={form.deliveryStreet.message} />
			)}
			{showDoorCodeInput && (
				<TextInput
					id="delivery-doorcode"
					placeholder={t.doorCode}
					value={form.deliveryDoorCode.value}
					maxLength={DOOR_CODE_MAX_LENGTH}
					onKeyDown={onKeyPress}
					autoFocus={showDoorCodeInput}
					nextElement="delivery-zip"
					onBlur={onBlur}
					onChange={form.deliveryDoorCode.handleOnChange}
					toggleableLabel={{
						text: t.removeDoorCode,
						onClick: () => {
							setShowDoorCodeInput(false);
							form.deliveryDoorCode.setValue('');
						},
						id: 'zip-toggle-remove',
					}}
				/>
			)}
			<TextInput
				className={halfRow}
				id="delivery-zip"
				autocomplete="delivery-zip"
				placeholder={t.zip}
				value={form.deliveryZip.value}
				maxLength={8}
				isValid={form.deliveryZip.isValid}
				status={form.deliveryZip.type}
				onFocus={() => setDeliveryZipIsFocused(true)}
				onBlur={(e) => {
					if (onBlur) onBlur(e);
					form.deliveryZip.validate();
					setDeliveryZipIsFocused(false);
				}}
				onChange={form.deliveryZip.handleOnChange}
				onKeyDown={onKeyPress}
				nextElement="delivery-city"
			/>
			<TextInput
				className={halfRow}
				id="delivery-city"
				autocomplete="address-level2"
				placeholder={t.city}
				value={form.deliveryCity.value}
				maxLength={64}
				isValid={form.deliveryCity.isValid}
				status={form.deliveryCity.type}
				onBlur={(e) => {
					if (onBlur) onBlur(e);
					form.deliveryCity.validate();
				}}
				onChange={form.deliveryCity.handleOnChange}
				onKeyDown={onKeyPress}
			/>
			<div className={halfRow}>
				{form.deliveryZip.type !== 'success' && (
					<InputAlertMessage type={form.deliveryZip.type} message={form.deliveryZip.message} />
				)}
			</div>
			<div className={halfRow}>
				{form.deliveryCity.type !== 'success' && (
					<InputAlertMessage type={form.deliveryCity.type} message={form.deliveryCity.message} />
				)}
			</div>
			{addressSuggestions.length > 0 && (
				<div id="address-suggestions-list" className="grid-halv-row">
					<AutocompleteList
						items={filterUniqueZip(addressSuggestions, form.deliveryZip.value) as object[]}
						template={filterUniqueZip(addressSuggestions, form.deliveryZip.value)!.map(
							(address) => `${asZip(address.postcode)} ${address.locality}`
						)}
						onComplete={(item: AddressSuggestion) => {
							setShowAddressSuggestions(false);
							form.deliveryZip.setValueAndValidate(asZip(item.postcode));
							form.deliveryCity.setValueAndValidate(item.locality);
						}}
						display={showAddressSuggestions}
						className={`address-suggestions delivery ${
							form.deliveryZip.isValid && form.deliveryCity.isValid
								? 'no-validation-message'
								: 'validation-message'
						}`}
					/>
				</div>
			)}
			<TextInput
				id="phone"
				placeholder={t.smsNotificationPhone}
				value={form.deliveryPhone.value}
				maxLength={64}
				onBlur={(e) => {
					form.deliveryPhone.validate(true, { deliveryType: deliveryType });
					if (onBlur) onBlur(e);
				}}
				status={form.deliveryPhone.type}
				isValid={form.deliveryPhone.isValid}
				onChange={form.deliveryPhone.handleOnChange}
				onKeyDown={onKeyPress}
			/>
			{form.deliveryPhone.type !== 'success' && (
				<InputAlertMessage type={form.deliveryPhone.type} message={form.deliveryPhone.message} />
			)}
		</InputGrid>
	);
};
